import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

/**
 * As três rotas sem sessão da pré-inscrição (`permitAll` na API).
 *
 * O método importa: `POST .../inscritos` é o envio da ficha pelo link público, mas
 * `GET .../inscritos` é a lista da gestão e **precisa** do token. Isentar pela URL inteira
 * tiraria o `Authorization` da tela de gestão e ela voltaria 401.
 */
function ehPreInscricaoPublica(metodo: string, url: string): boolean {
  if (/\/pre-inscricoes\/[^/]+\/(publico|vaga)(\?|$)/.test(url)) return true;
  return metodo === 'POST' && /\/pre-inscricoes\/[^/]+\/inscritos(\?|$)/.test(url);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const isPublicApi =
    req.url.includes('autenticacao/login') ||
    req.url.includes('autenticacao/recuperar-senha') ||
    req.url.includes('/inscricao-catequese') ||
    ehPreInscricaoPublica(req.method, req.url);
  const token = auth.getToken();
  const hitsOurApi = req.url.startsWith(environment.api);

  // AUTH-07: token com prazo vencido conta como sessão ausente. Não se envia — a API hoje
  // responde 500, e não 401, a token recusado (PEND-11), e aí o AUTH-06 abaixo nunca dispara.
  if (!isPublicApi && hitsOurApi && !auth.isAuthenticated()) {
    auth.clearSession();
    if (!router.url.startsWith('/login')) {
      router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
    }
    return throwError(
      () => new HttpErrorResponse({ status: 401, statusText: 'Não autenticado', url: req.url }),
    );
  }

  const authReq =
    token && !isPublicApi
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authReq).pipe(
    catchError((err: unknown) => {
      // AUTH-06: 401 e 403 encerram a sessão e levam ao login com returnUrl — exceto na
      // própria chamada de login, onde 401 é senha errada e 403 é credencial válida sem
      // acesso, e os dois precisam chegar ao formulário como mensagem (AUTH-09).
      if (
        err instanceof HttpErrorResponse &&
        (err.status === 401 || err.status === 403) &&
        !isPublicApi &&
        req.url.startsWith(environment.api)
      ) {
        auth.clearSession();
        if (!router.url.startsWith('/login')) {
          router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
        }
      }
      return throwError(() => err);
    }),
  );
};
